<template>
    <div class="slideshow-settings">

        <!-- Draggable Preview using actual IdleScreen -->
        <Teleport to="body">
            <div v-if="isPreviewActive" ref="previewWindow" class="preview-container" :style="previewPosition">
                <div class="drag-handle-top" @mousedown="startDrag"></div>
                <button class="close-btn" @click="togglePreview" aria-label="Close Preview">×</button>
                <div class="preview-content">
                    <IdleScreen :orderStarted="false" :enabled="true" :orderTypes="[]" :previewMode="true"
                        :backgroundType="'slideshow'" :slideshowImages="images" :slideshowInterval="rotationSpeed"
                        :transitionEffect="sanitizedTransitionValue"
                        :showText="config?.screensaverShowText" :startOrderText="config?.text?.startOrder"
                        :startOrderSubtext="config?.text?.startOrderSubtext" :config="config" :idleTimeSeconds="0"
                        :shouldSync="false" class="preview-idle-screen" />
                </div>
                <div class="resize-handle" @mousedown.stop="startResize"></div>
            </div>
        </Teleport>

        <!-- Main Content -->
        <div class="slideshow-content">
            <!-- Header -->
            <div class="slideshow-header mb-3">
                <div class="d-flex align-items-center">
                    <h6 class="mb-0 fw-semibold">Slideshow Images</h6>
                    <span class="badge bg-primary ms-2">{{ images.length }}</span>
                </div>
                <div class="header-actions">
                    <button v-if="images.length" type="button" class="btn btn-outline-primary btn-sm" @click="togglePreview">
                        <i class="fas" :class="isPreviewActive ? 'fa-eye-slash' : 'fa-eye'"></i>
                        Preview
                    </button>
                    <button v-if="images.length" type="button" class="btn btn-outline-danger btn-sm" @click="removeAllImages">
                        Clear All
                    </button>
                </div>
            </div>

            <!-- Image Gallery with Upload Zone -->
            <div class="gallery-section mb-3">
                <div class="image-grid" :class="{ 'has-images': images.length }">
                    <!-- Existing Images -->
                    <draggable v-model="images" @end="updateImageOrder" class="image-list" handle=".drag-handle"
                        :item-key="'url'">
                        <template #item="{ element, index }">
                            <div class="image-tile">
                                <div class="image-container">
                                    <div class="drag-handle" :class="{ 'is-dragging': isDragging }">
                                        <i class="fas fa-grip-vertical"></i>
                                        <span class="image-number">{{ isDragging ? '?' : index + 1 }}</span>
                                    </div>
                                    <img :src="element.url" :alt="`Image ${index + 1}`" class="slide-image" />
                                    <button type="button" class="remove-btn" @click="removeImage(index)">
                                        <i class="fas fa-times"></i>
                                    </button>
                                </div>
                            </div>
                        </template>
                    </draggable>

                    <!-- Upload Zone -->
                    <div class="upload-tile" :class="{ 'upload-active': isDragOver, 'full-width': !images.length }">
                        <label v-if="!isUploading" for="slideshowImageUpload" class="upload-zone"
                            @dragover.prevent="onDragOver" @dragenter.prevent="onDragEnter" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
                            <div class="upload-content">
                                <i class="fas fa-cloud-upload-alt upload-icon"></i>
                                <div class="upload-text">{{ images.length ? 'Add more' : 'Drop images here or click to browse' }}</div>
                                <div v-if="!images.length" class="upload-hint">JPEG, PNG, GIF, BMP • Max 5MB each</div>
                            </div>
                            <input type="file" class="file-input" id="slideshowImageUpload" @change="handleSlideshowImageUpload"
                                accept="image/*" multiple>
                        </label>

                        <div v-else class="upload-progress">
                            <div class="progress mb-2">
                                <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
                                    style="width: 100%"></div>
                            </div>
                            <div class="text-muted">Uploading...</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Settings Cards -->
            <div v-if="images.length" class="settings-section">
                <div class="settings-grid">
                    <!-- Speed Card -->
                    <div class="setting-card">
                        <div class="setting-header">
                            <span class="setting-title">Speed</span>
                            <span class="setting-value">{{ rotationSpeed }}s</span>
                        </div>
                        <div class="speed-controls">
                            <button type="button" class="speed-btn" :class="{ 'active': rotationSpeed === 10 }" @click="setSpeed(10)">Slow</button>
                            <button type="button" class="speed-btn" :class="{ 'active': rotationSpeed === 7 }" @click="setSpeed(7)">Medium</button>
                            <button type="button" class="speed-btn" :class="{ 'active': rotationSpeed === 4 }" @click="setSpeed(4)">Fast</button>
                        </div>
                        <button type="button" class="custom-toggle" @click="toggleCustomSpeed">
                            {{ showCustomSpeed ? 'Hide custom' : 'Custom speed' }}
                        </button>
                        <div v-if="showCustomSpeed" class="custom-controls">
                            <input type="range" class="custom-range" v-model.number="rotationSpeed" min="2" max="20" step="1" @change="updateRotationSpeed">
                            <input type="number" class="custom-input" min="2" max="20" step="1" v-model.number="rotationSpeed" @change="updateRotationSpeed">
                        </div>
                    </div>

                    <!-- Transition Card -->
                    <div class="setting-card">
                        <div class="setting-header">
                            <span class="setting-title">Transition</span>
                        </div>
                        <select class="transition-select" :value="sanitizedTransitionValue" @change="updateTransition($event.target.value)">
                            <option v-for="opt in currentTransitionOptions" :key="opt" :value="opt">{{ toTitleCase(opt.replace(/-/g, ' ')) }}</option>
                        </select>
                        <div class="advanced-toggle">
                            <label class="toggle-label">
                                <input type="checkbox" :checked="showAdvanced" @change="showAdvanced = $event.target.checked">
                                <span class="toggle-text">More effects</span>
                            </label>
                        </div>
                        <div v-if="showAdvanced" class="advanced-options">
                            <label class="sync-label">
                                <input type="checkbox" :checked="config.screensaverSync" @change="updateSync($event.target.checked)">
                                <span class="sync-text">Sync across terminals</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <br />
</template>

<script>
import draggable from 'vuedraggable';
import IdleScreen from '../../features/IdleScreen.vue';
import { toTitleCase } from '@/utils/helpers';

export default {
    components: {
        draggable,
        IdleScreen
    },
    props: {
        config: {
            type: Object,
            required: true
        }
    },
    emits: ['update', 'validation'],
    data() {
        return {
            images: [...(this.config.screensaverImages || [])].map(img => ({ ...img, isUpdating: false })),
            rotationSpeed: this.config.screensaverRotationSpeed || 7,
            isUploading: false,
            isDragOver: false,
            isDragging: false,
            dragStartIndex: null,
            isPreviewActive: false,
            previewPosition: {
                left: '20px',
                top: '20px'
            },
            dragOffset: { x: 0, y: 0 },
            currentImageIndex: 0,
            previewInterval: null,
            isResizing: false,
            resizeStartX: 0,
            resizeStartY: 0,
            startWidth: 0,
            startHeight: 0,
            showAdvanced: false,
            showCustomSpeed: false,
            basicTransitionOptions: ['fade', 'slide-left', 'zoom'],
            advancedTransitionOptions: [
                'fade',
                'zoom',
                'slide-left',
                'slide-up',
                'ken-burns',
                'spiral',
                'blinds',
                'morph',
                'glitch',
                'flip',
                'shockwave',
                'prism',
                'kaleidoscope',
                'matrix',
                'ripple'
            ]
        };
    },
    methods: {
        toTitleCase,
        speedBtnClass(sec) {
            return this.rotationSpeed === sec ? 'btn-primary' : 'btn-outline-secondary';
        },
        setSpeed(sec) {
            this.rotationSpeed = sec;
            this.updateRotationSpeed();
        },
        toggleCustomSpeed() {
            this.showCustomSpeed = !this.showCustomSpeed;
        },
        async handleSlideshowImageUpload(event) {
            const files = event.target.files;
            await this.processFiles(files);
        },
        async onDrop(event) {
            this.isDragOver = false;
            const files = event.dataTransfer && event.dataTransfer.files ? event.dataTransfer.files : [];
            if (!files || !files.length) return;
            await this.processFiles(files);
        },
        onDragEnter() {
            this.isDragOver = true;
        },
        onDragOver() {
            this.isDragOver = true;
        },
        onDragLeave() {
            this.isDragOver = false;
        },
        async processFiles(fileList) {
            this.isUploading = true;
            for (let i = 0; i < fileList.length; i++) {
                const file = fileList[i];
                if (!file || !file.type?.startsWith('image/')) continue;
                if (file.size > 5 * 1024 * 1024) {
                    alert('File size exceeds the 5MB limit. Please choose a smaller file.');
                    continue;
                }
                try {
                    const response = await this.uploadFile(file);
                    this.images.push({
                        url: response.url,
                        duration: this.rotationSpeed
                    });
                    this.emitConfigUpdate();
                } catch (error) {
                    alert('Failed to upload image. Please try again.');
                }
            }
            this.isUploading = false;
        },
        async uploadFile(file) {
            const formData = new FormData();
            formData.append('file', file);
            try {
                const response = await this.$store.dispatch('admin/uploadLogo', formData);
                return response;
            } catch (error) {
                console.error('Error uploading file:', error);
                throw error;
            }
        },
        removeImage(index) {
            this.images.splice(index, 1);
            this.emitConfigUpdate();
        },
        removeAllImages() {
            if (!this.images.length) return;
            if (confirm('Remove all slideshow images?')) {
                this.images = [];
                this.emitConfigUpdate();
            }
        },
        updateImageOrder(event) {
            const newIndex = event.newIndex;
            const oldIndex = event.oldIndex;

            // Mark items that need to be animated
            this.images.forEach((img, index) => {
                if (oldIndex < newIndex) {
                    if (index >= oldIndex && index <= newIndex) {
                        img.isUpdating = true;
                    }
                } else {
                    if (index >= newIndex && index <= oldIndex) {
                        img.isUpdating = true;
                    }
                }
            });

            // Remove animation class after transition
            setTimeout(() => {
                this.images.forEach(img => {
                    img.isUpdating = false;
                });
            }, 300);

            this.emitConfigUpdate();
            this.isDragging = false;
        },
        updateRotationSpeed() {
            // keep images durations aligned to rotationSpeed for simplicity
            this.images = this.images.map(img => ({
                ...img,
                duration: this.rotationSpeed
            }));

            this.emitConfigUpdate();
        },
        togglePreview() {
            this.isPreviewActive = !this.isPreviewActive;
            if (this.isPreviewActive) {
                this.startPreviewRotation();
            } else {
                this.stopPreviewRotation();
            }
        },

        startPreviewRotation() {
            this.currentImageIndex = 0;
            this.previewInterval = setInterval(() => {
                this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length;
            }, this.rotationSpeed * 1000);
        },
        stopPreviewRotation() {
            if (this.previewInterval) {
                clearInterval(this.previewInterval);
                this.previewInterval = null;
            }
        },
        startDrag(event) {
            if (event.target.closest('.close-btn')) return;

            this.isDragging = true;
            const rect = this.$refs.previewWindow.getBoundingClientRect();
            this.dragOffset = {
                x: event.clientX - rect.left,
                y: event.clientY - rect.top
            };

            document.addEventListener('mousemove', this.handleDrag);
            document.addEventListener('mouseup', this.stopDrag);
        },
        handleDrag(event) {
            if (!this.isDragging) return;

            this.previewPosition = {
                left: `${event.clientX - this.dragOffset.x}px`,
                top: `${event.clientY - this.dragOffset.y}px`
            };
        },
        stopDrag() {
            this.isDragging = false;
            document.removeEventListener('mousemove', this.handleDrag);
            document.removeEventListener('mouseup', this.stopDrag);
        },
        emitConfigUpdate() {
            this.$emit('update', {
                ...this.config,
                screensaverImages: this.images,
                screensaverRotationSpeed: this.rotationSpeed,
            });
        },
        startResize(event) {
            event.preventDefault();
            event.stopPropagation();
            this.isResizing = true;
            this.resizeStartX = event.clientX;
            this.resizeStartY = event.clientY;
            this.startWidth = this.$refs.previewWindow.offsetWidth;
            this.startHeight = this.$refs.previewWindow.offsetHeight;

            document.addEventListener('mousemove', this.handleResize);
            document.addEventListener('mouseup', this.stopResize);
        },
        handleResize(event) {
            if (!this.isResizing) return;

            const minWidth = 320;
            const maxWidth = window.innerWidth * 0.8;
            
            let width = Math.min(maxWidth, Math.max(minWidth, 
                this.startWidth + (event.clientX - this.resizeStartX)));
            
            let height = Math.round(width * (9/16));

            this.$refs.previewWindow.style.width = `${width}px`;
            this.$refs.previewWindow.style.height = `${height}px`;
        },
        stopResize() {
            this.isResizing = false;
            document.removeEventListener('mousemove', this.handleResize);
            document.removeEventListener('mouseup', this.stopResize);
        },
        updateTransition(value) {
            this.$emit('update', {
                ...this.config,
                screensaverTransition: value
            });
        },
        updateSync(value) {
            this.$emit('update', {
                ...this.config,
                screensaverSync: value
            });
        }
    },
    computed: {
        hasImages() {
            return this.images && this.images.length > 0;
        },
        currentPreviewImage() {
            return this.images[this.currentImageIndex]?.url
        },
        currentTransitionOptions() {
            return this.showAdvanced ? this.advancedTransitionOptions : this.basicTransitionOptions;
        },
        sanitizedTransitionValue() {
            const val = this.config?.screensaverTransition || 'fade';
            return this.currentTransitionOptions.includes(val) ? val : 'fade';
        }
    },
    watch: {
        hasImages: {
            immediate: true,
            handler(hasImages) {
                this.$emit('validation', hasImages);
            }
        },
        'draggable:start'() {
            this.isDragging = true;
        },
        config: {
            handler(newConfig) {
                console.log('Config changed:', newConfig);
            },
            deep: true
        }
    },
    beforeUnmount() {
        this.stopPreviewRotation();
    }
};
</script>

<style scoped>
.slideshow-settings {
    max-width: 100%;
}

.slideshow-content {
    padding: 0;
}

/* Header */
.slideshow-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e9ecef;
}

.header-actions {
    display: flex;
    gap: 0.5rem;
}

/* Image Gallery */
.gallery-section {
    margin-bottom: 1.5rem;
}

.image-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 1rem;
}

.image-grid.has-images {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
}

.image-list {
    display: contents;
}

.image-tile {
    aspect-ratio: 1;
}

/* Upload Zone */
.upload-tile {
    aspect-ratio: 1;
    grid-column: span 1;
}

.upload-tile.full-width {
    grid-column: 1 / -1;
    aspect-ratio: auto;
}

.upload-tile.upload-active {
    transform: scale(1.02);
}

.upload-zone {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border: 2px dashed #dee2e6;
    border-radius: 12px;
    background: #fafbfc;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 120px;
}

.upload-tile.full-width .upload-zone {
    min-height: 180px;
    padding: 2rem;
}

.upload-zone:hover {
    border-color: #0d6efd;
    background: #f0f7ff;
}

.upload-tile.upload-active .upload-zone {
    border-color: #0d6efd;
    background: #f0f7ff;
}

.upload-content {
    text-align: center;
}

.upload-icon {
    font-size: 2rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
}

.upload-tile.full-width .upload-icon {
    font-size: 2.5rem;
    margin-bottom: 1rem;
}

.upload-text {
    font-size: 0.9rem;
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.25rem;
}

.upload-tile.full-width .upload-text {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
}

.upload-hint {
    font-size: 0.8rem;
    color: #6c757d;
}

.upload-tile.full-width .upload-hint {
    font-size: 0.9rem;
}

.upload-progress {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 1rem;
    text-align: center;
}

.upload-tile.full-width .upload-progress {
    padding: 2rem;
}

.file-input {
    display: none;
}

.image-container {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    overflow: hidden;
    background: #f8f9fa;
    transition: transform 0.2s ease;
}

.image-container:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.slide-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.drag-handle {
    position: absolute;
    top: 6px;
    left: 6px;
    width: 28px;
    height: 24px;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: grab;
    transition: all 0.2s ease;
    z-index: 2;
    padding: 2px;
}

.drag-handle:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.05);
}

.drag-handle i {
    color: white;
    font-size: 0.6rem;
    margin-bottom: 1px;
}

.image-number {
    color: white;
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 1;
}

.remove-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 24px;
    height: 24px;
    background: rgba(220, 53, 69, 0.9);
    border: none;
    border-radius: 50%;
    color: white;
    font-size: 0.75rem;
    cursor: pointer;
    opacity: 0;
    transition: all 0.2s ease;
    z-index: 2;
}

.image-container:hover .remove-btn {
    opacity: 1;
}

.remove-btn:hover {
    background: #dc3545;
    transform: scale(1.1);
}

/* Settings Cards */
.settings-section {
    margin-top: 1.5rem;
}

.settings-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

@media (max-width: 768px) {
    .settings-grid {
        grid-template-columns: 1fr;
    }
}

.setting-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 1.25rem;
    transition: all 0.3s ease;
}

.setting-card:hover {
    border-color: #dee2e6;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.setting-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.setting-title {
    font-weight: 600;
    color: #495057;
}

.setting-value {
    font-size: 0.9rem;
    color: #6c757d;
    background: #f8f9fa;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
}

/* Speed Controls */
.speed-controls {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
}

.speed-btn {
    flex: 1;
    padding: 0.5rem;
    border: 1px solid #dee2e6;
    background: white;
    border-radius: 6px;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.speed-btn:hover {
    border-color: #0d6efd;
    background: #f0f7ff;
}

.speed-btn.active {
    background: #0d6efd;
    border-color: #0d6efd;
    color: white;
}

.custom-toggle {
    background: none;
    border: none;
    color: #0d6efd;
    font-size: 0.9rem;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
}

.custom-controls {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid #e9ecef;
}

.custom-range {
    flex: 1;
}

.custom-input {
    width: 60px;
    padding: 0.25rem 0.5rem;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    font-size: 0.9rem;
}

/* Transition Controls */
.transition-select {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    background: white;
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
}

.advanced-toggle {
    margin-bottom: 0.5rem;
}

.toggle-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 0.9rem;
}

.toggle-label input {
    margin-right: 0.5rem;
}

.toggle-text {
    color: #6c757d;
}

.advanced-options {
    padding-top: 0.75rem;
    border-top: 1px solid #e9ecef;
}

.sync-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 0.9rem;
}

.sync-label input {
    margin-right: 0.5rem;
}

.sync-text {
    color: #6c757d;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 3rem 1rem;
    color: #6c757d;
}

.empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
}

.empty-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #495057;
}

.empty-text {
    font-size: 0.9rem;
}

/* Preview Container (unchanged from original) */
.preview-container {
    position: fixed;
    width: 600px;
    height: 337.5px;
    background: #000;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 9999;
    overflow: hidden;
    cursor: move;
    resize: none;
    user-select: none;
    -webkit-user-select: none;
}

.drag-handle-top {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 20px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 0 0 4px 4px;
    cursor: move;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
}

.close-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    color: white;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    font-size: 1.5rem;
    line-height: 1;
    padding: 4px 12px;
    cursor: pointer;
    opacity: 0.8;
    transition: opacity 0.2s ease;
    z-index: 2;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    min-height: 32px;
}

.close-btn:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.3);
}

.preview-content {
    position: relative;
    height: 100%;
    overflow: hidden;
    background: #000;
    cursor: default;
    z-index: 0;
    user-select: none;
    -webkit-user-select: none;
}

.preview-content :deep(*) {
    user-select: none !important;
    -webkit-user-select: none !important;
}

.preview-idle-screen {
    transform-origin: top left;
    width: 100% !important;
    height: 100% !important;
}

.preview-idle-screen :deep(.screensaver) {
    position: relative !important;
    width: 100% !important;
    height: 100% !important;
}

.preview-idle-screen :deep(.order-here) {
    font-size: 3rem !important;
    line-height: 3rem !important;
}

.preview-idle-screen :deep(.tap-screen) {
    font-size: 1rem !important;
    padding-top: 0.5rem !important;
}

.preview-idle-screen :deep(.screensaver-logo) {
    width: 3rem !important;
    height: 3rem !important;
    margin-bottom: 0.5rem !important;
}

.resize-handle {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 24px;
    height: 24px;
    cursor: se-resize;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>